import { useEffect } from 'react';
import { useLazyGetEntitlementsQuery } from '~/redux/api/routes/subscriptions';
import { AuthAccessor, LocalStorageApi } from '~/utils/storage';
import {
  EntitlementFeatures,
  EntitlementSources,
} from '~/redux/api/routes/subscriptions/constants';
import { LAST_ACTIVE_KEY, USER_EMAIL, USER_ID } from '~/utils/storage/contants';
import type { Entitlements } from '~/redux/api/routes/subscriptions/models';

const INACTIVITY_LIMIT = 12 * 60 * 60 * 1000;

export const payWallUrl = import.meta.env.VITE_PAYWALL_URL;

export const getUserAccessType = (
  entitlements: Entitlements
): 'free' | 'none' | 'paid' => {
  if (!entitlements?.length) return 'none';

  const freeAccessSources = [
    EntitlementSources.support,
    EntitlementSources.ad_campaign,
  ];

  let hasPaidAccess = false;
  let hasFreeAccess = false;

  for (const entitlement of entitlements) {
    if (entitlement.feature_id === EntitlementFeatures.app_access) {
      if (freeAccessSources.includes(entitlement.source)) {
        hasFreeAccess = true;
      } else {
        hasPaidAccess = true;
      }
    }
  }

  if (hasPaidAccess) return 'paid';

  if (hasFreeAccess) return 'free';

  return 'none';
};

export const isUserHasGuidesToAccess = (entitlements: Entitlements) =>
  entitlements?.length &&
  entitlements.some((entitlement) =>
    entitlement.feature_id.startsWith('guide_')
  );

export const isUserHasCoursesToAccess = (entitlements: Entitlements) =>
  entitlements?.length &&
  entitlements.some((entitlement) =>
    entitlement.feature_id.startsWith('course_')
  );

export const usePayWallOnInactive = () => {
  const [fetchEntitlements] = useLazyGetEntitlementsQuery();

  useEffect(() => {
    const isUserAuthorized = !!AuthAccessor.getAccessToken();

    if (!isUserAuthorized) return;

    const updateLastActiveTimeAndCheckSubscription =
      async (): Promise<boolean> => {
        try {
          const { entitlements } = await fetchEntitlements().unwrap();

          const userAccessType = getUserAccessType(entitlements);

          if (userAccessType === 'none') {
            return false;
          }

          LocalStorageApi?.setItem(LAST_ACTIVE_KEY, Date.now());
          return true;
        } catch (error) {
          console.error(error);
        }
      };

    const handleVisibilityChange = async (): Promise<void> => {
      const lastActive = Number(
        LocalStorageApi?.getItem(LAST_ACTIVE_KEY) || '0'
      );

      const now = Date.now();

      if (document.visibilityState === 'visible') {
        if (lastActive && now - lastActive < INACTIVITY_LIMIT) return;

        const hasSubscription =
          await updateLastActiveTimeAndCheckSubscription();

        if (!hasSubscription) {
          const userId = LocalStorageApi?.getItem(USER_ID);
          const userEmail = LocalStorageApi?.getItem(USER_EMAIL);
          AuthAccessor.clearTokens();
          LocalStorageApi?.removeItem(USER_ID);
          LocalStorageApi?.removeItem(USER_EMAIL);
          window.location.assign(
            `${payWallUrl}?user_id=${userId}&email=${userEmail}`
          );
        }
      }
    };

    document.addEventListener('visibilitychange', () =>
      handleVisibilityChange()
    );
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchEntitlements]);
};
